import { useState } from "react";
import {
  DndContext, DragOverlay, PointerSensor, closestCenter, useDraggable, useDroppable, useSensor, useSensors,
} from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { channelMeta } from "@/components/ChannelBadge";
import { GripVertical, Trash2, Loader2, ArrowDown, MousePointerClick, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export const TONE_META = {
  friendly: { label: "Friendly", color: "#4ADE80", bg: "rgba(74,222,128,0.14)" },
  direct: { label: "Direct", color: "#7FAEF9", bg: "rgba(79,142,247,0.14)" },
  firm: { label: "Firm", color: "#FBBF24", bg: "rgba(245,158,11,0.14)" },
  urgent: { label: "Urgent", color: "#F87171", bg: "rgba(239,68,68,0.14)" },
};

const WAITS = [
  { v: 0, l: "Immediately" }, { v: 6, l: "After 6 hours" }, { v: 12, l: "After 12 hours" },
  { v: 24, l: "After 24 hours" }, { v: 48, l: "After 2 days" }, { v: 72, l: "After 3 days" }, { v: 168, l: "After 1 week" },
];
const CHANNELS = ["slack", "teams", "email", "text", "call"];

let uidCounter = 0;
const mkStep = (channel) => ({ uid: `step-${Date.now()}-${uidCounter++}`, channel, wait_hours: 24, tone: "friendly" });

const PaletteBlock = ({ channel, onAdd }) => {
  const m = channelMeta(channel);
  const Icon = m.icon;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `pal-${channel}` });
  return (
    <button ref={setNodeRef} {...attributes} {...listeners} onClick={() => onAdd(channel)}
      data-testid={`palette-block-${channel}`}
      className={`flex w-full cursor-grab items-center gap-2 rounded-xl border px-3 py-2.5 text-[12.5px] font-medium transition-colors active:cursor-grabbing ${isDragging ? "opacity-40" : ""}`}
      style={{ background: m.bg, borderColor: m.border, color: m.color }}>
      <Icon size={14} /> {m.label}
      <GripVertical size={12} className="ml-auto opacity-50" />
    </button>
  );
};

const StepCard = ({ step, index, onChange, onRemove }) => {
  const m = channelMeta(step.channel);
  const Icon = m.icon;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: step.uid });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }}
      className={isDragging ? "z-10 opacity-60" : ""}>
      {index > 0 && (
        <div className="flex items-center gap-1.5 py-1.5 pl-10 text-[10px] text-[#6b6b6b]">
          <ArrowDown size={10} /> if still no response…
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-[#242424] bg-[#161616] p-2.5" data-testid={`strategy-step-${index}`}>
        <button {...attributes} {...listeners} data-testid={`strategy-step-handle-${index}`}
          className="flex h-8 w-6 cursor-grab items-center justify-center rounded text-[#555] hover:text-white active:cursor-grabbing">
          <GripVertical size={13} />
        </button>
        <span className="flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-[12px] font-medium"
          style={{ background: m.bg, color: m.color }}>
          <Icon size={13} /> {m.label}
        </span>
        <Select value={String(step.wait_hours)} onValueChange={(v) => onChange({ ...step, wait_hours: Number(v) })}>
          <SelectTrigger className="h-8 w-36 border-[#2a2a2a] bg-[#111] text-[11.5px]" data-testid={`strategy-step-wait-${index}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="border-[#222] bg-[#161616] text-white">
            {WAITS.map((w) => <SelectItem key={w.v} value={String(w.v)}>{w.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={step.tone} onValueChange={(v) => onChange({ ...step, tone: v })}>
          <SelectTrigger className="h-8 w-32 border-[#2a2a2a] bg-[#111] text-[11.5px]" data-testid={`strategy-step-tone-${index}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="border-[#222] bg-[#161616] text-white">
            {Object.entries(TONE_META).map(([k, t]) => (
              <SelectItem key={k} value={k}><span style={{ color: t.color }}>{t.label} tone</span></SelectItem>
            ))}
          </SelectContent>
        </Select>
        <button onClick={onRemove} data-testid={`strategy-step-remove-${index}`}
          className="ml-auto flex h-8 w-8 items-center justify-center rounded-lg text-[#6b6b6b] hover:bg-[#1c1c1c] hover:text-[#F87171]">
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
};

export const StrategyBuilder = ({ strategy, onSaved, onCancel }) => {
  const [name, setName] = useState(strategy?.name || "");
  const [description, setDescription] = useState(strategy?.description || "");
  const [steps, setSteps] = useState(() => (strategy?.steps || []).map((s) => ({ ...mkStep(s.channel), ...s, uid: `step-${Date.now()}-${uidCounter++}` })));
  const [saving, setSaving] = useState(false);
  const [activeDrag, setActiveDrag] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const { setNodeRef: seqRef, isOver: seqOver } = useDroppable({ id: "sequence" });

  const addStep = (channel) => setSteps((st) => [...st, mkStep(channel)]);

  const onDragEnd = ({ active, over }) => {
    setActiveDrag(null);
    if (!over) return;
    const aid = String(active.id);
    const oid = String(over.id);
    if (aid.startsWith("pal-")) {
      const channel = aid.slice(4);
      const idx = steps.findIndex((s) => s.uid === oid);
      const step = mkStep(channel);
      setSteps((st) => (idx === -1 ? [...st, step] : [...st.slice(0, idx + 1), step, ...st.slice(idx + 1)]));
    } else if (aid !== oid) {
      const from = steps.findIndex((s) => s.uid === aid);
      const to = steps.findIndex((s) => s.uid === oid);
      if (from !== -1 && to !== -1) setSteps((st) => arrayMove(st, from, to));
    }
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Give the strategy a name"); return; }
    if (steps.length === 0) { toast.error("Add at least one step — drag a channel block in"); return; }
    setSaving(true);
    const payload = { name: name.trim(), description, steps: steps.map(({ channel, wait_hours, tone }) => ({ channel, wait_hours, tone })) };
    try {
      if (strategy?.id) await api.patch(`/strategies/${strategy.id}`, payload);
      else await api.post("/strategies", payload);
      toast.success(strategy?.id ? `Strategy updated: ${payload.name}` : `Strategy created: ${payload.name}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save strategy");
    } finally {
      setSaving(false);
    }
  };

  const dragMeta = activeDrag?.startsWith("pal-") ? channelMeta(activeDrag.slice(4)) : null;

  return (
    <div className="rounded-[14px] border border-[rgba(79,142,247,0.25)] bg-[#111111] p-5 card-shadow" data-testid="strategy-builder">
      <div className="flex flex-wrap items-center gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Strategy name — e.g. Quarter-end crunch"
          data-testid="strategy-builder-name"
          className="h-9 max-w-xs border-[#2a2a2a] bg-[#161616] text-[13px] font-medium text-white placeholder:text-[#555]" />
        <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="One-line description (optional)"
          data-testid="strategy-builder-description"
          className="h-9 min-w-0 flex-1 border-[#2a2a2a] bg-[#161616] text-[12px] text-white placeholder:text-[#555]" />
        <div className="ml-auto flex items-center gap-2">
          <button onClick={onCancel} data-testid="strategy-builder-cancel"
            className="h-9 rounded-lg px-3 text-[12px] text-[#A3A3A3] hover:bg-[#1c1c1c] hover:text-white">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="strategy-builder-save"
            className="flex h-9 items-center gap-1.5 rounded-lg btn-gradient px-4 text-[12.5px] font-medium text-white disabled:opacity-40">
            {saving && <Loader2 size={13} className="animate-spin" />} {strategy?.id ? "Save changes" : "Create strategy"}
          </button>
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter}
        onDragStart={({ active }) => setActiveDrag(String(active.id))} onDragEnd={onDragEnd}>
        <div className="mt-4 grid gap-4 lg:grid-cols-[200px_1fr]">
          {/* palette */}
          <div>
            <div className="mb-2 flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-[#6b6b6b]">
              <MousePointerClick size={11} /> Channels — drag or click
            </div>
            <div className="space-y-2" data-testid="strategy-palette">
              {CHANNELS.map((c) => <PaletteBlock key={c} channel={c} onAdd={addStep} />)}
            </div>
          </div>

          {/* sequence */}
          <div>
            <div className="mb-2 flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-[#6b6b6b]">
              <Zap size={11} /> Escalation sequence — runs top to bottom
            </div>
            <div ref={seqRef} data-testid="strategy-sequence"
              className={`min-h-[140px] rounded-xl border border-dashed p-3 transition-colors ${seqOver ? "border-[#4F8EF7] bg-[rgba(79,142,247,0.05)]" : "border-[#2a2a2a]"}`}>
              <div className="mb-2 flex items-center gap-1.5 rounded-lg bg-[rgba(74,222,128,0.08)] px-3 py-2 text-[11px] text-[#4ADE80]">
                <Zap size={11} /> Trigger: employee owes a receipt or an answer
              </div>
              <SortableContext items={steps.map((s) => s.uid)} strategy={verticalListSortingStrategy}>
                <div className="space-y-1">
                  {steps.map((s, i) => (
                    <StepCard key={s.uid} step={s} index={i}
                      onChange={(next) => setSteps((st) => st.map((x) => (x.uid === s.uid ? next : x)))}
                      onRemove={() => setSteps((st) => st.filter((x) => x.uid !== s.uid))} />
                  ))}
                </div>
              </SortableContext>
              {steps.length === 0 && (
                <div className="py-8 text-center text-[11.5px] text-[#555]" data-testid="strategy-sequence-empty">
                  Drag a channel block here (or click one) to add the first step
                </div>
              )}
            </div>
          </div>
        </div>

        <DragOverlay>
          {dragMeta && (
            <div className="flex items-center gap-2 rounded-xl border px-3 py-2.5 text-[12.5px] font-medium shadow-xl"
              style={{ background: "#161616", borderColor: dragMeta.border, color: dragMeta.color }}>
              <dragMeta.icon size={14} /> {dragMeta.label}
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </div>
  );
};
